import { token } from '@atlaskit/tokens';
import { type StylesConfig } from '@databases/atlaskit/Select';
import { Z_INDEX_MENU_PORTAL_TARGET } from '@databases/react/menuPortal';

const compactSelectIndicatorsGutter = '56px';
const compactSelectValueContainerMaxWidth = `calc(100% - ${compactSelectIndicatorsGutter})`;

export const baseSelectStyleConfig: StylesConfig<any> = {
  menu: (base) => ({
    ...base,
    width: '100%',
    minWidth: '260px',
    position: 'relative',
    boxShadow: 'none',
    margin: '0'
  }),
  menuList: (base) => ({
    ...base,
    padding: '0'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0'
  }),
  control: (base) => ({
    ...base,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    paddingLeft: token('space.150', '.75rem'),
    paddingRight: token('space.150', '.75rem'),
    // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
    font: token('font.body', '14px')
  }),
  placeholder: (base) => ({
    ...base,
    left: token('space.300', '1.5rem'),
    paddingLeft: token('space.150', '.75rem'),
    overflow: 'hidden',
    position: 'absolute',
    width: 'calc(100% - 1.5rem)'
  }),
  input: (base) => ({
    ...base,
    overflow: 'hidden',
    position: 'absolute',
    width: 'calc(100% - 1.5rem)',
    marginLeft: token('space.100', '0.5rem'),
    // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
    font: token('font.body', '14px')
  }),
  option: (base) => ({
    ...base,
    // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
    font: token('font.body', '14px')
  })
};

export const isRenderedInSidePanelStyling: StylesConfig<any> = {
  menu: (base) => ({
    ...base,
    width: '100%',
    minWidth: '260px'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0'
  }),
  control: (base) => ({
    ...base,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    border: 'none',
    // Override @atlaskit/select's font.body.large (16px) on narrow viewports
    // to prevent iOS Safari zoom. Match the cell's 14px font size.
    fontSize: '14px'
  }),
  placeholder: (base) => ({
    ...base,
    paddingLeft: token('space.150', '.75rem')
  }),
  input: (base) => ({
    ...base,
    marginLeft: token('space.150', '.75rem'),
    '> div > input': {
      width: '100% !important'
    },
    // Override @atlaskit/select's font.body.large (16px) on narrow viewports
    // to prevent iOS Safari zoom. Match the cell's 14px font size.
    fontSize: '14px'
  }),
  option: (base) => ({
    ...base,
    // Override @atlaskit/select's font.body.large (16px) on narrow viewports
    // to prevent iOS Safari zoom. Match the cell's 14px font size.
    fontSize: '14px'
  })
};

export const customPlaceholderAndControlSpacingStyling: StylesConfig<any> = {
  placeholder: (base) => ({
    ...base,
    left: 'auto',
    marginLeft: token('space.150', '.75rem'),
    position: 'relative',
    overflow: 'visible',
    width: 'auto'
  }),
  valueContainer: (base) => ({
    ...base,
    display: 'flex',
    paddingLeft: token('space.150', '.75rem')
  }),
  input: (base) => ({
    ...base,
    position: 'relative',
    overflow: 'visible',
    width: 'auto',
    marginLeft: '-0.3rem',
    // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
    font: token('font.body', '14px')
  }),
  control: (base) => ({
    ...base,
    paddingLeft: 0,
    paddingRight: 0,
    // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
    font: token('font.body', '14px')
  })
};

const compactSelectStyleConfig: StylesConfig<any> = {
  ...baseSelectStyleConfig,
  menu: (base) => ({
    ...base,
    backgroundColor: token('elevation.surface.overlay'),
    borderColor: token('color.border'),
    borderRadius: token('radius.medium'),
    borderStyle: 'solid',
    borderWidth: token('border.width'),
    width: '100%',
    minWidth: '100%',
    boxShadow: token('elevation.shadow.overlay'),
    marginBlockStart: token('space.050', '4px'),
    marginBlockEnd: '0',
    zIndex: 2
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: Z_INDEX_MENU_PORTAL_TARGET
  }),
  control: (base, state) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    backgroundColor: token('color.background.input'),
    borderColor: state.isFocused ? token('color.border.focused') : token('color.border'),
    borderRadius: token('radius.medium'),
    borderStyle: 'solid',
    borderWidth: token('border.width'),
    boxSizing: 'border-box',
    boxShadow: 'none',
    flexWrap: 'wrap',
    height: 'auto',
    minHeight: '34px',
    outline: 'none',
    paddingBlock: token('space.050', '4px'),
    paddingInline: token('space.100', '8px'),
    position: 'relative',
    font: token('font.body', '14px'),
    '&:hover': {
      borderColor: state.isFocused ? token('color.border.focused') : token('color.border')
    }
  }),
  valueContainer: (base) => ({
    ...base,
    alignContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 auto',
    flexWrap: 'wrap',
    columnGap: token('space.050', '4px'),
    rowGap: token('space.025', '2px'),
    maxWidth: compactSelectValueContainerMaxWidth,
    minHeight: '24px',
    minWidth: '0',
    paddingBlock: '0',
    paddingInline: '0',
    overflow: 'hidden'
  }),
  input: (base, state) => {
    const hasInputValue = Boolean(state.selectProps.inputValue);
    const hasActiveInput = Boolean(hasInputValue || state.selectProps.menuIsOpen);

    return {
      ...base,
      alignItems: 'center',
      display: 'inline-flex',
      flex: hasInputValue ? '1 1 auto' : hasActiveInput ? '0 0 1px' : '0 0 0',
      height: hasActiveInput ? '22px' : '0',
      marginBlock: '0',
      marginInlineStart: token('space.050'),
      marginInlineEnd: '0',
      minWidth: hasInputValue ? '24px' : hasActiveInput ? '1px' : '0',
      position: 'relative',
      overflow: 'visible',
      padding: '0',
      top: 'auto',
      transform: 'none',
      width: hasInputValue ? 'auto' : hasActiveInput ? '1px' : '0',
      font: token('font.body', '14px'),
      '& input': {
        height: hasActiveInput ? '22px' : '0',
        margin: '0',
        padding: '0'
      }
    };
  },
  multiValue: (base) => ({
    ...base,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'inline-flex',
    flex: '0 1 auto',
    maxWidth: '100%',
    minHeight: '24px',
    minWidth: '0',
    overflow: 'hidden',
    marginBlock: '0'
  }),
  multiValueLabel: (base) => ({
    ...base,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'inline-flex',
    flex: '1 1 0',
    maxWidth: '100%',
    minHeight: '22px',
    minWidth: '0',
    overflow: 'hidden',
    paddingBlock: '0',
    paddingInlineEnd: token('space.025', '2px'),
    whiteSpace: 'nowrap',
    '& a': {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'inline-flex',
      marginBlock: '0',
      maxWidth: '100%',
      minHeight: '24px',
      minWidth: '0',
      paddingBlock: '0'
    },
    '& a > .text': {
      alignItems: 'center',
      display: 'inline-flex',
      minWidth: '0'
    },
    '& [data-testid*="databases.components.content-link.abstract-link"]': {
      alignItems: 'center',
      display: 'inline-flex',
      maxWidth: '100%',
      minHeight: '24px',
      minWidth: '0'
    }
  }),
  singleValue: (base) => ({
    ...base,
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 1 auto',
    margin: '0',
    maxWidth: '100%',
    minHeight: '24px',
    minWidth: '0',
    overflow: 'hidden',
    position: 'relative',
    textOverflow: 'ellipsis',
    top: 'auto',
    transform: 'none',
    whiteSpace: 'nowrap',
    '& a': {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'inline-flex',
      marginBlock: '0',
      maxWidth: '100%',
      minHeight: '24px',
      minWidth: '0',
      paddingBlock: '0'
    },
    '& a > .text': {
      alignItems: 'center',
      display: 'inline-flex',
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& [data-testid*="databases.components.content-link.abstract-link"]': {
      alignItems: 'center',
      display: 'inline-flex',
      maxWidth: '100%',
      minHeight: '24px',
      minWidth: '0',
      overflow: 'hidden'
    }
  }),
  multiValueRemove: (base) => ({
    ...base,
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    justifyContent: 'center',
    minHeight: '24px',
    minWidth: '20px',
    paddingBlock: '0',
    paddingInline: token('space.025', '2px')
  }),
  indicatorsContainer: (base) => ({
    ...base,
    alignItems: 'center',
    bottom: '0',
    display: 'flex',
    position: 'absolute',
    right: token('space.025', '2px'),
    top: '0'
  }),
  placeholder: (base) => ({
    ...base,
    alignItems: 'center',
    display: 'inline-flex',
    left: 'auto',
    margin: '0',
    minHeight: '22px',
    paddingLeft: '0',
    position: 'relative',
    top: 'auto',
    transform: 'none',
    overflow: 'hidden',
    width: 'auto'
  }),
  option: (base) => ({
    ...base,
    minHeight: '32px',
    font: token('font.body', '14px')
  })
};

export const compactOptionSelectStyleConfig: StylesConfig<any> = {
  ...compactSelectStyleConfig,
  menuList: (base) => ({
    ...base,
    paddingBlock: token('space.075', '6px')
  }),
  option: (base) => ({
    ...base,
    alignItems: 'center',
    display: 'flex',
    minHeight: '32px',
    paddingBlock: '0',
    font: token('font.body', '14px')
  })
};

export const compactTagSelectStyleConfig: StylesConfig<any> = {
  ...compactOptionSelectStyleConfig,
  control: (base, state) => ({
    ...compactOptionSelectStyleConfig.control?.(base, state),
    minHeight: '32px'
  }),
  valueContainer: (base) => ({
    ...base,
    alignContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: token('space.050', '4px'),
    rowGap: token('space.025', '2px'),
    maxWidth: compactSelectValueContainerMaxWidth,
    minHeight: '24px',
    paddingBlock: '0',
    paddingInline: '0',
    overflow: 'hidden'
  }),
  multiValue: (base) => ({
    ...base,
    margin: '0'
  })
};
